import React from 'react';

const serviceLineAliases: Record<string, string> = {
  'Bedside Pro': 'Inpatient Professional Coding'
};

// Checks if a configuration value's serviceLines match the given service line.
// The includeWildcard flag (default true) controls whether to count a wildcard match.
export function serviceLineMatches(
  configValue: CMxAPI.ConfigurationValue,
  serviceLineName: string,
  includeWildcard: boolean = true
): boolean {
  const normalizedServiceLine =
    serviceLineAliases[serviceLineName] || serviceLineName;
  return (
    configValue.serviceLines?.includes(serviceLineName) ||
    configValue.serviceLines?.includes(normalizedServiceLine) ||
    (includeWildcard && configValue.serviceLines?.includes('*'))
  );
}

// Retrieves configuration values for a given service line and tenant.
// It prioritizes an exact match (excluding wildcard) before falling back to wildcard matches.
export function getConfigValueForServiceLine(
  values: CMxAPI.ConfigurationValue[],
  serviceLineName: string,
  tenantId: string
): string[] {
  // 1. Exact match on tenantId and service line using alias matching, excluding wildcard.
  let matches = values.filter(
    v =>
      v.tenantId === tenantId && serviceLineMatches(v, serviceLineName, false)
  );
  if (matches.length > 0) return matches.map(v => v.value);

  // 2. Exact tenantId with wildcard service line.
  matches = values.filter(
    v => v.tenantId === tenantId && v.serviceLines?.includes('*')
  );
  if (matches.length > 0) return matches.map(v => v.value);

  // 3. Wildcard tenantId with exact service line (excluding wildcard).
  matches = values.filter(
    v => v.tenantId === '*' && serviceLineMatches(v, serviceLineName, false)
  );
  if (matches.length > 0) return matches.map(v => v.value);

  // 4. Wildcard tenantId and wildcard service line.
  matches = values.filter(
    v => v.tenantId === '*' && v.serviceLines?.includes('*')
  );
  if (matches.length > 0) return matches.map(v => v.value);

  return [];
}

// Formats the configuration display value. If multiple values exist, they are wrapped in square brackets.
// Optionally includes a label ("Value:" or "MultiValue:") based on the includeLabel flag.
export function formatConfigDisplayValue(
  value: string | string[] | null | undefined,
  includeLabel: boolean = false
): JSX.Element | string {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return <span style={{ color: 'gray' }}>null</span>;
  }
  if (Array.isArray(value)) {
    if (value.length > 1) {
      const formatted = `[${value.join(', ')}]`;
      return includeLabel ? `MultiValue: ${formatted}` : formatted;
    }
    return includeLabel ? `Value: ${value[0]}` : value[0];
  }
  return includeLabel ? `Value: ${value}` : value;
}

// Formats a timestamp into a localized date string, or returns "Unknown" if the timestamp is not provided.
export function formatTimestamp(
  timestamp: number | string | null | undefined
): string {
  if (!timestamp) return 'Unknown';
  return new Date(+timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
